import {
  ArrowTrendingUpIcon,
  BuildingOffice2Icon,
  FaceSmileIcon,
  RocketLaunchIcon,
  TicketIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { GaugeIcon } from "lucide-react";
import { HTMLAttributes, forwardRef } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { cn } from "src/utils/ui/ui";

type Props = {};

export default function AnalyticsTabs({}: Props) {
  const { tab } = useParams();
  if (tab === "projects") {
    return <ProjectAnalyticsTabs />;
  } else if (tab === "time-tracking") {
    return <TimeTrackingAnalyticsTabs />;
  } else if (tab === "project-templates") {
    return <TemplatesAnalyticsTabs />;
  } else if (tab === "key-moments") {
    return <KeyMomentTab />;
  } else if (tab === "financials") {
    return <FinancialsAnalyticsTabs />;
  } else if (tab === "csat") {
    return <CSATTab />;
  } else {
    return <Navigate to="/analytics/projects" replace />;
  } 
}

type CardProps = {} & HTMLAttributes<HTMLDivElement>;

export const AnalyticsCard = forwardRef<HTMLDivElement, CardProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        {...props}
        className={cn(
          "h-40 cursor-pointer rounded-lg border border-gray-200/70 bg-white p-6 shadow-sm transition-all hover:border-gray-300/70",
          className
        )}
      >
        {children}
      </div>
    );
  }
);

function ProjectAnalyticsTabs() {
  const navigate = useNavigate();
  return (
    <div className="h-full space-y-12 bg-gray-50 p-6 px-8">
      <div className="space-y-2.5">
        <div className="text-sm text-gray-800">Active Projects</div>

        <div className="grid gap-2 lg:grid-cols-2 2xl:grid-cols-3">
        <AnalyticsCard
          onClick={() => {
            navigate("/analytics/projects/momentum");
          }}
        >
          <div className="flex items-center space-x-1.5">
            <div className="flex flex-col gap-3.5">
              <ArrowTrendingUpIcon className="h-[18px] w-[18px] text-primary" />
              <div className="text-base font-medium leading-none text-gray-800">
                Momentum
              </div>
            </div>
          </div>
          <div className="pt-[8px] text-sm font-light text-gray-500">
            Identify which projects are slowing down
          </div>
        </AnalyticsCard>
        {/* <AnalyticsCard
          onClick={() => {
            navigate("/analytics/project-pipeline-review");
          }}
        >
          <div className="flex flex-col gap-3.5">
            <FunnelIcon className="h-[18px] w-[18px] text-primary" />
            <div className="text-base font-medium leading-none text-gray-800">
              Pipeline Review
            </div>
          </div>
          <div className="pt-[8px] text-sm font-light text-gray-500">
            View the distribution of your projects across stages. See which ones
            are behind schedule.
          </div>
        </AnalyticsCard> */}
        <AnalyticsCard
          onClick={() => {
            navigate("/analytics/projects/milestones");
          }}
        >
          <div className="flex flex-col gap-3.5">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
              className="h-[18px] w-[18px]"
            >
              <path
                d="M8 1 L15 8 L8 15 L1 8 Z"
                fill="none"
                stroke-width="1"
                className="stroke-primary/70"
              />

              <path d="M8 4 L12 8 L8 12 L4 8 Z" className="fill-primary" />
            </svg>
            <div className="text-base font-medium leading-none text-gray-800">
              Milestones
            </div>
          </div>
          <div className="pt-[8px] text-sm font-light text-gray-500">
            Compare milestones across projects.
          </div>
        </AnalyticsCard>
        <AnalyticsCard
          onClick={() => {
            navigate("/analytics/projects/progress-vs-duration");
          }}
        >
          <div className="flex flex-col gap-3.5">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              className="h-[18px] w-[18px] text-primary"
            >
              <line x1="19" x2="5" y1="5" y2="19" />
              <circle cx="6.5" cy="6.5" r="2.5" />
              <circle cx="17.5" cy="17.5" r="2.5" />
            </svg>
            <div className="text-base font-medium leading-none text-gray-800">
              Progress vs Duration
            </div>
          </div>
          <div className="pt-[8px] text-sm font-light leading-normal text-gray-500">
            Contrast project progress vs duration to spot projects that need
            attention
          </div>
        </AnalyticsCard>
        
        </div>
      </div>
      <div className="space-y-2.5">
        <div className="text-sm text-gray-800">Completed Projects</div>

        <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
        <AnalyticsCard
          onClick={() => {
            navigate("/analytics/projects/average-completion-time");
          }}
        >
          <div className="flex items-center space-x-1.5">
            <div className="flex flex-col gap-3.5">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                className="h-[18px] w-[18px] text-primary"
              >
                <path d="M21.801 10A10 10 0 1 1 17 3.335" />
                <path d="m9 11 3 3L22 4" />
              </svg>
              <div className="text-base font-medium leading-none text-gray-800">
                Average Completion Time
              </div>
            </div>
          </div>
          <div className="pt-[8px] text-sm font-light leading-normal text-gray-500">
            Analyze the average project completion times across the last 4
            quarters
          </div>
        </AnalyticsCard>
        </div>
      </div>
      </div>
  );
}

function CSATTab() {
  const navigate = useNavigate();
  return (
    <div className="grid h-full bg-gray-50 p-6 px-8 md:grid-cols-3">
      <AnalyticsCard
        onClick={() => {
          navigate("/analytics/csat/overview");
        }}
      >
        <div className="flex flex-col gap-3.5">
          <FaceSmileIcon className="h-[18px] w-[18px] text-primary" />
          <div className="text-base font-medium leading-none text-gray-800">
            CSAT Overview
          </div>
        </div>
        <div className="pt-2.5 text-sm font-light text-gray-600">
          Study the CSAT scores across projects and teams.
        </div>
      </AnalyticsCard>
    </div>
  );
}
function KeyMomentTab() {
  const navigate = useNavigate();
  return (
    <div className="grid h-full bg-gray-50 p-6 px-8 md:grid-cols-3">
      <AnalyticsCard
        onClick={() => {
          navigate("/analytics/key-moments/key-intervals");
        }}
      >
        <div className="flex flex-col gap-3.5">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-[18px] w-[18px] text-primary"
          >
            {/* First circle on left side */}
            <circle cx="3" cy="8" r="2" />

            {/* Connecting line between circles */}
            <line x1="5" y1="8" x2="11" y2="8" />

            {/* Second circle on right side */}
            <circle cx="13" cy="8" r="2" />
          </svg>
          {/* <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            className="h-[18px] w-[18px] text-primary"
          >
            <circle cx="19" cy="5" r="2" />
            <circle cx="5" cy="19" r="2" />
            <path d="M5 17A12 12 0 0 1 17 5" />
          </svg> */}
          <div className="text-base font-medium leading-none text-gray-800">
            Key Intervals
          </div>
        </div>
        <div className="pt-2.5 text-sm font-light text-gray-600">
          View the key intervals of your projects.
        </div>
      </AnalyticsCard>
    </div>
  );
}

function TemplatesAnalyticsTabs() {
  const navigate = useNavigate();
  return (
    <div className="grid h-full bg-gray-50 p-6 px-8 md:grid-cols-3">
      <AnalyticsCard
        onClick={() => {
          navigate("/analytics/project-template-performance");
        }}
      >
        <div className="flex items-center space-x-1.5">
          {/* <MapIcon className="text-primary-500 h-[18px] w-[18px]" /> */}
          <div className="text-base font-medium leading-none text-gray-800">
            Project Template Performance
          </div>
        </div>
        <div className="pt-2 text-sm font-light text-gray-600">
          View the performance of your project templates.
        </div>
      </AnalyticsCard>
    </div>
  );
}

function TimeTrackingAnalyticsTabs() {
  const navigate = useNavigate();
  return (
    <div className="h-full space-y-12 bg-gray-50 p-6 px-8">
      <div className="space-y-2.5">
        <div className="text-sm text-gray-800">Key Reports</div>

        <div className="grid gap-2 lg:grid-cols-2 2xl:grid-cols-3">
          <AnalyticsCard
            onClick={() => {
              navigate("/analytics/time-tracking/utilization");
            }}
          >
            <div className="flex items-center space-x-1.5">
              <div className="flex flex-col gap-3.5">
                <GaugeIcon className="h-[18px] w-[18px] text-primary" />
                <div className="text-base font-medium leading-none text-gray-800">
                  Utilization
                </div>
              </div>
            </div>
            <div className="pt-[8px] text-sm font-light leading-normal text-gray-500">
              See your team's utilization trend by day, week, month or quarter
            </div>
          </AnalyticsCard>
          <AnalyticsCard
            onClick={() => {
              navigate("/analytics/time-tracking/team");
            }}
          >
            <div className="flex items-center space-x-1.5">
              <div className="flex flex-col gap-3.5">
                <UsersIcon className="h-[18px] w-[18px] text-primary" />
                <div className="text-base font-medium leading-none text-gray-800">
                  Team
                </div>
              </div>
            </div>
            <div className="pt-[8px] text-sm font-light leading-normal text-gray-500">
              Breakdown time tracked by team member
            </div>
          </AnalyticsCard>
          <AnalyticsCard
            onClick={() => {
              navigate("/analytics/time-tracking/projects");
            }}
          >
            <div className="flex items-center space-x-1.5">
              <div className="flex flex-col gap-3.5">
                <RocketLaunchIcon className="h-[18px] w-[18px] text-primary" />
                <div className="text-base font-medium leading-none text-gray-800">
                  Projects
                </div>
              </div>
            </div>
            <div className="pt-[8px] text-sm font-light leading-normal text-gray-500">
              Breakdown time tracked by project
            </div>
          </AnalyticsCard>
          <AnalyticsCard
            onClick={() => {
              navigate("/analytics/time-tracking/companies");
            }}
          >
            <div className="flex items-center space-x-1.5">
              <div className="flex flex-col gap-3.5">
                <BuildingOffice2Icon className="h-[18px] w-[18px] text-primary" />
                <div className="text-base font-medium leading-none text-gray-800">
                  Companies
                </div>
              </div>
            </div>
            <div className="pt-[8px] text-sm font-light leading-normal text-gray-500">
              Breakdown time tracked by company
            </div>
          </AnalyticsCard>
        </div>
      </div>
      <div className="space-y-2.5">
        <div className="text-sm text-gray-800">Explore</div>

        <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
          <AnalyticsCard
            onClick={() => {
              navigate("/analytics/time-tracking/entries");
            }}
          >
            <div className="flex items-center space-x-1.5">
              <div className="flex flex-col gap-3.5">
                <TicketIcon className="h-[18px] w-[18px] text-primary" />
                <div className="text-base font-medium leading-none text-gray-800">
                  Entries
                </div>
              </div>
            </div>
            <div className="pt-[8px] text-sm font-light leading-normal text-gray-500">
              View all time entries
            </div>
          </AnalyticsCard>
        </div>
      </div>
      {/* <AnalyticsCard
        onClick={() => {
          navigate("/analytics/utilization");
        }}
      >
        <div className="flex items-center space-x-1.5">
          <div className="text-base font-medium leading-none text-gray-800">
            Utilization Trend
          </div>
        </div>
        <div className="pt-2 text-sm font-light text-gray-600">
          View how your team's utilization is trending by day, week, month or
          quarter.
        </div>
      </AnalyticsCard>
      <AnalyticsCard
        onClick={() => {
          navigate("/analytics/user-time-tracking");
        }}
      >
        <div className="flex items-center space-x-1.5">
          <div className="text-base font-medium leading-none text-gray-800">
            User Time Tracking
          </div>
        </div>
        <div className="pt-2 text-sm font-light text-gray-600">
          View your team's capacity, allocated time and tracked time to better
          understand the context of their utilization numbers.
        </div>
      </AnalyticsCard>
      <AnalyticsCard
        onClick={() => {
          navigate("/analytics/project-time-tracking");
        }}
      >
        <div className="flex items-center space-x-1.5">
          <div className="text-base font-medium leading-none text-gray-800">
            Project Time Tracking
          </div>
        </div>
        <div className="pt-2 text-sm font-light text-gray-600">
          Breakdown the budgeted time, allocated time and tracked time for
          projects.
        </div>
      </AnalyticsCard> */}
      {/* <AnalyticsCard
        onClick={() => {
          navigate("/analytics/timesheet-submissions");
        }}
      >
        <div className="flex items-center space-x-1.5">
          <div className="text-base font-medium leading-none text-gray-800">
            Timesheet Submissions
          </div>
        </div>
        <div className="pt-2 text-sm font-light text-gray-600">
          See how many timesheets have been submitted by your team.
        </div>
      </AnalyticsCard> */}
    </div>
  );
}

function FinancialsAnalyticsTabs() {
  const navigate = useNavigate();
  return (
    <div className="grid h-full gap-4 bg-gray-50 p-6 px-8 md:grid-cols-3">
      <AnalyticsCard
        onClick={() => {
          navigate("/analytics/project-financials");
        }}
      >
        <div className="flex items-center space-x-1.5">
          <div className="text-base font-medium leading-none text-gray-800">
            Project Financials
          </div>
        </div>
        <div className="pt-2 text-sm font-light text-gray-600">
          View the revenue, cost and margins for your projects.
        </div>
      </AnalyticsCard>
    </div>
  );
}
